import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { addDays, format, startOfWeek } from 'date-fns';
import type { ScheduleBlock, TaskUI } from '@flexiplanner/types';
import { numberToPriority, taskToUI } from '@flexiplanner/types';
import { useScheduleStore } from '@/stores/scheduleStore';
import type { CalendarEvent } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import { ScheduledTaskBlock } from './ScheduledTaskBlock';
import { DroppableTimeSlot } from './DroppableTimeSlot';
import { CalendarCommitmentEditor } from './CalendarCommitmentEditor';
import { CalendarEmptyState } from './CalendarEmptyState';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { api } from '@/lib/api';
import { getCachedCalendarEvents, setCachedCalendarEvents } from '@/lib/calendarCache';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { CalendarClock, ChevronLeft, ChevronRight, Minus, Plus, RefreshCw } from 'lucide-react';
import { formatDateHeader } from '@/lib/utils';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function toLocalDateString(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function asWeekStart(date: Date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

function densitySlotHeight(density: 'compact' | 'balanced' | 'spacious') {
  if (density === 'compact') return 56;
  if (density === 'spacious') return 96;
  return 72;
}

function getDropMinute(event: DragEndEvent, fallbackMinute: number, slotHeight: number) {
  const translated = event.active.rect.current.translated;
  const initial = event.active.rect.current.initial;
  const activeRect = translated || initial;
  const overRect = event.over?.rect;
  if (!activeRect || !overRect) return fallbackMinute;

  const pointerY = activeRect.top + activeRect.height / 2;
  const relative = Math.max(0, Math.min(slotHeight, pointerY - overRect.top));
  return Math.max(0, Math.min(59, Math.round((relative / slotHeight) * 60)));
}

export function DayView() {
  const { toast } = useToast();
  const [slotHeight, setSlotHeight] = useState(72);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFreeingTime, setIsFreeingTime] = useState(false);
  const [editingCalendarEvent, setEditingCalendarEvent] = useState<CalendarEvent | null>(null);
  const [editingTask, setEditingTask] = useState<TaskUI | null>(null);

  const currentDate = useScheduleStore(s => s.currentDate);
  const setCurrentDate = useScheduleStore(s => s.setCurrentDate);
  const setView = useScheduleStore(s => s.setView);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  const blocks = useScheduleStore(s => s.blocks);
  const calendarEvents = useScheduleStore(s => s.calendarEvents);
  const tasks = useTasksStore(s => s.tasks);
  const setTasks = useTasksStore(s => s.setTasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTask = useTasksStore(s => s.deleteTask);
  const workHours = useUserStore(s => s.settings.workHours);
  const calendarDensity = useUserStore(s => s.settings.calendarDensity);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 10 },
    })
  );

  const dateKey = toLocalDateString(currentDate);
  const weekStartKey = toLocalDateString(asWeekStart(currentDate));
  const workingWindow = useMemo(() => parseWorkingWindow(workHours), [workHours]);

  useEffect(() => {
    setView('day');
  }, [setView]);

  useEffect(() => {
    setSlotHeight(densitySlotHeight(calendarDensity));
  }, [calendarDensity]);

  useEffect(() => {
    void refreshDay();
  }, [dateKey]);

  const scheduledTaskBlocks = useMemo<ScheduleBlock[]>(() => {
    return tasks
      .filter(t => t.scheduledStart && t.scheduledEnd)
      .map(t => ({
        id: `task-${t.id}`,
        taskId: t.id,
        title: t.title,
        startTime: t.scheduledStart!,
        endTime: t.scheduledEnd!,
        isGhost: false,
        priority: t.priority,
        requiredEnergy: t.energyLevel,
        stateProfile: t.stateProfile,
        customAttributes: t.customAttributes,
      }));
  }, [tasks]);

  const calendarBlocks = useMemo<ScheduleBlock[]>(() => {
    return calendarEvents.map(event => ({
      id: `gcal-${event.id}`,
      taskId: event.id,
      title: event.title,
      startTime: event.start,
      endTime: event.end,
      isGhost: false,
      isCalendarEvent: true,
      isFreeTimeHold: event.isFlexHold,
      priority: event.isFlexHold ? 5 : 4,
      stateProfile: event.stateProfile,
      customAttributes: event.customAttributes,
      requiredEnergy: event.stateProfile?.required_productivity_energy ?? undefined,
    }));
  }, [calendarEvents]);

  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const calendarEventById = useMemo(
    () => new Map(calendarEvents.map(event => [event.id, event])),
    [calendarEvents]
  );

  const blocksByHour = useMemo(() => {
    const merged = new Map<string, ScheduleBlock>();
    const taskIds = new Set(tasks.map(task => task.id));

    [...blocks, ...scheduledTaskBlocks, ...calendarBlocks].forEach(block => {
      if (block.isGhost && block.taskId && !taskIds.has(block.taskId)) return;
      if (toLocalDateString(new Date(block.startTime)) !== dateKey) return;
      merged.set(block.id, block);
    });

    const grouped = new Map<number, ScheduleBlock[]>();
    for (const block of merged.values()) {
      const hour = new Date(block.startTime).getHours();
      const existing = grouped.get(hour);
      if (existing) existing.push(block);
      else grouped.set(hour, [block]);
    }
    return grouped;
  }, [blocks, scheduledTaskBlocks, calendarBlocks, tasks, dateKey]);
  const hasCalendarContent = useMemo(
    () => Array.from(blocksByHour.values()).some(hourBlocks => hourBlocks.length > 0) || isRefreshing,
    [blocksByHour, isRefreshing]
  );
  const pinchHandlers = usePinchZoom({
    setValue: setSlotHeight,
    min: 40,
    max: 168,
  });

  async function refreshDay() {
    setIsRefreshing(true);
    try {
      await api.schedule.rollover();

      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = addDays(dayStart, 1);

      const calendarStart = dayStart.toISOString();
      const calendarEnd = dayEnd.toISOString();
      const cachedCalendarEvents = getCachedCalendarEvents(calendarStart, calendarEnd);
      if (cachedCalendarEvents) setCalendarEvents(cachedCalendarEvents);

      const calendarPromise = api.calendar.getEvents(calendarStart, calendarEnd)
        .then(calendarResponse => {
          if (calendarResponse.success && calendarResponse.data) {
            const normalizedEvents = normalizeCalendarEvents(calendarResponse.data);
            setCalendarEvents(normalizedEvents);
            setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
          }
        })
        .catch(error => {
          console.warn('Background Google Calendar refresh failed', error);
        });

      const [tasksResponse, scheduleResponse] = await Promise.all([
        api.tasks.list(),
        api.schedule.getGhostBlocks(weekStartKey),
      ]);

      let freshTaskIds = new Set(tasks.map(task => task.id));
      if (tasksResponse.success && tasksResponse.data) {
        const tasksList = Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : (tasksResponse.data as any).tasks;
        if (tasksList) {
          const nextTasks = tasksList.map(taskToUI);
          setTasks(nextTasks);
          freshTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
        }
      }

      if (scheduleResponse.success && scheduleResponse.data) {
        setBlocks(normalizeScheduleBlocks(scheduleResponse.data, freshTaskIds));
        setGhostBlocks(normalizeGhostBlocks(scheduleResponse.data, freshTaskIds));
        if (!cachedCalendarEvents && scheduleResponse.data.gcal_events?.length) {
          const normalizedEvents = normalizeCalendarEvents(scheduleResponse.data.gcal_events || []);
          setCalendarEvents(normalizedEvents);
          setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
        }
      }

      void calendarPromise;
    } finally {
      setIsRefreshing(false);
    }
  }

  const goToPreviousDay = () => setCurrentDate(addDays(currentDate, -1));
  const goToNextDay = () => setCurrentDate(addDays(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.data.current?.type === 'ScheduledTask' && over.data.current?.type === 'TimeSlot') {
      const block = active.data.current.block as ScheduleBlock;
      if (block.isGhost || block.isCalendarEvent || block.isFreeTimeHold) return;

      const targetDateKey = over.data.current.dateKey;
      const targetHour = over.data.current.hour;
      const oldStart = new Date(block.startTime);
      const durationMs = new Date(block.endTime).getTime() - oldStart.getTime();
      const targetMinute = getDropMinute(event, oldStart.getMinutes(), slotHeight);

      const newStart = new Date(targetDateKey + 'T00:00:00');
      newStart.setHours(targetHour, targetMinute, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);

      updateTask(block.taskId, {
        scheduledStart: newStart.toISOString(),
        scheduledEnd: newEnd.toISOString(),
      });

      try {
        await api.tasks.update(block.taskId, {
          do_date: newStart.toISOString(),
          do_date_end: newEnd.toISOString(),
        });
      } catch (err) {
        console.error('Failed to reschedule activity', err);
        toast({
          title: 'Move failed',
          description: 'The activity could not be moved.',
          variant: 'destructive',
        });
        void refreshDay();
      }
    }
  };

  async function freeUpLater() {
    const time = window.prompt('Start time to protect?', '4pm');
    if (!time) return;
    const duration = window.prompt('How much time?', '60 minutes') || '60 minutes';
    setIsFreeingTime(true);
    try {
      const response = await api.nlp.command(`free up ${duration} at ${time} today`);
      if (!response.success) throw new Error(response.error?.message || 'Could not free time');
      toast({
        title: response.data?.notification?.title || 'Time protected',
        description: response.data?.notification?.message || 'Your schedule was adjusted.',
      });
      await refreshDay();
    } catch (error) {
      toast({
        title: 'Could not free time',
        description: error instanceof Error ? error.message : 'Try a different window.',
        variant: 'destructive',
      });
    } finally {
      setIsFreeingTime(false);
    }
  }

  function handleCalendarEventSaved(updatedEvent: CalendarEvent) {
    setCalendarEvents(calendarEvents.map(event => (event.id === updatedEvent.id ? updatedEvent : event)));
    setEditingCalendarEvent(null);
    toast({ title: updatedEvent.isFlexHold ? 'Protected time updated' : 'Commitment updated' });
    void refreshDay();
  }

  async function handleSaveTask(updates: Partial<TaskUI>) {
    if (!editingTask) return;

    try {
      const response = await api.tasks.update(editingTask.id, {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        custom_attributes: updates.customAttributes,
      });
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI((response.data as any).task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (error) {
      console.error('Failed to save activity', error);
      updateTask(editingTask.id, updates);
    }
  }

  async function handleDeleteTask() {
    if (!editingTask) return;
    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to delete activity', error);
    }
  }

  async function handleRescheduleTomorrow() {
    if (!editingTask) return;
    const response = await api.schedule.scheduleTaskTomorrow(editingTask.id);
    if (!response.success) {
      toast({
        title: 'Could not move activity',
        description: response.error?.message || 'No optimal slot was found tomorrow.',
        variant: 'destructive',
      });
      return;
    }
    if (response.data?.task) updateTask(editingTask.id, taskToUI(response.data.task));
    setEditingTask(null);
    await refreshDay();
    toast({
      title: 'Moved to tomorrow',
      description: response.data?.notification?.message || 'The scheduler picked the best available slot.',
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b bg-background/95 p-2 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPreviousDay}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={goToNextDay}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => void refreshDay()} disabled={isRefreshing}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={freeUpLater} disabled={isFreeingTime}>
              <CalendarClock className="mr-2 h-4 w-4" />
              Free Up
            </Button>
          </div>

          <h2 className="text-lg font-semibold sm:text-xl">{formatDateHeader(currentDate)}</h2>

          <div className="flex w-fit items-center gap-1 rounded-md border bg-background/80 p-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Zoom calendar out"
              onClick={() => setSlotHeight(value => Math.max(40, value - 12))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-14 text-center text-xs text-muted-foreground">
              {Math.round((slotHeight / 72) * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Zoom calendar in"
              onClick={() => setSlotHeight(value => Math.min(156, value + 12))}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="native-scroll temporal-grid relative min-h-0 flex-1 overflow-auto" {...pinchHandlers}>
        {!hasCalendarContent && <CalendarEmptyState onRefresh={() => void refreshDay()} isRefreshing={isRefreshing} />}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="w-full px-2 pb-8 sm:px-4">
            <div className="sticky top-0 z-10 grid grid-cols-[56px_minmax(0,1fr)] border-b bg-background/95 py-2 sm:grid-cols-[80px_minmax(0,1fr)]">
              <div />
              <div className="text-sm font-medium text-muted-foreground">
                {format(currentDate, 'EEEE, MMMM d')}
              </div>
            </div>
            {HOURS.map(hour => {
              const hourBlocks = blocksByHour.get(hour) || [];
              const boundaryMinutes = getWorkingBoundaryMinutes(hour, workingWindow);
              const outsideWorkingHours = isOutsideWorkingHour(hour, workingWindow);

              return (
                <div key={hour} className="grid grid-cols-[56px_minmax(0,1fr)] border-b sm:grid-cols-[80px_minmax(0,1fr)]">
                  <div
                    className={[
                      'p-2 text-right text-xs text-muted-foreground sm:p-4 sm:text-sm',
                      boundaryMinutes.length > 0 ? 'border-t-2 border-t-foreground/80' : '',
                    ].join(' ')}
                  >
                    {hour === 0
                      ? '12 AM'
                      : hour < 12
                        ? `${hour} AM`
                        : hour === 12
                          ? '12 PM'
                          : `${hour - 12} PM`}
                  </div>

                  <DroppableTimeSlot
                    id={`slot-${dateKey}-${hour}`}
                    dateKey={dateKey}
                    hour={hour}
                    slotHeight={slotHeight}
                    workingBoundaryMinutes={boundaryMinutes}
                    isOutsideWorkingHours={outsideWorkingHours}
                  >
                    {hourBlocks.map(block => (
                      <ScheduledTaskBlock
                        key={block.id}
                        block={block}
                        task={block.taskId ? taskById.get(block.taskId) : undefined}
                        onClick={() => {
                          if (block.isCalendarEvent) {
                            const event = block.taskId ? calendarEventById.get(block.taskId) : undefined;
                            if (event) setEditingCalendarEvent(event);
                            return;
                          }
                          if (!block.isGhost) {
                            const task = block.taskId ? taskById.get(block.taskId) : undefined;
                            if (task) setEditingTask(task);
                          }
                        }}
                      />
                    ))}
                  </DroppableTimeSlot>
                </div>
              );
            })}
          </div>
        </DndContext>
      </div>

      <CalendarCommitmentEditor
        event={editingCalendarEvent}
        open={Boolean(editingCalendarEvent)}
        onClose={() => setEditingCalendarEvent(null)}
        onSave={handleCalendarEventSaved}
      />
      <TaskEditor
        task={editingTask}
        open={Boolean(editingTask)}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
        onRescheduleTomorrow={handleRescheduleTomorrow}
      />
    </div>
  );
}

type WorkingWindow = {
  startMinutes: number;
  endMinutes: number;
};

function parseWorkingWindow(workHours?: { start?: string; end?: string } | null): WorkingWindow {
  return {
    startMinutes: parseClockMinutes(workHours?.start, 9 * 60),
    endMinutes: parseClockMinutes(workHours?.end, 17 * 60),
  };
}

function parseClockMinutes(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const [hourText, minuteText = '0'] = value.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return fallback;
  return Math.max(0, Math.min(24 * 60, hour * 60 + minute));
}

function getWorkingBoundaryMinutes(hour: number, window: WorkingWindow): number[] {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return [window.startMinutes, window.endMinutes]
    .filter(minutes => minutes >= hourStart && minutes < hourEnd)
    .map(minutes => minutes - hourStart);
}

function isOutsideWorkingHour(hour: number, window: WorkingWindow): boolean {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return hourEnd <= window.startMinutes || hourStart >= window.endMinutes;
}

function normalizeScheduleBlocks(data: any, taskIds?: Set<string>): ScheduleBlock[] {
  const ghostBlocks = (data.ghost_blocks || [])
    .filter((block: any) => !taskIds || taskIds.has(block.task_id))
    .map((block: any) => ({
      id: block.id,
      taskId: block.task_id,
      title: block.title || 'Suggested activity',
      startTime: block.proposed_start,
      endTime: block.proposed_end,
      isGhost: true,
    }));

  const workSegments = (data.work_segments || []).map((segment: any) => ({
    id: segment.id,
    taskId: segment.task_id,
    title: segment.segment_type === 'pause' ? 'Free time' : segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: segment.segment_type === 'pause' ? 5 : 3,
    requiredEnergy: segment.segment_type === 'pause' ? 0 : undefined,
  }));

  return [...ghostBlocks, ...workSegments];
}

function normalizeGhostBlocks(data: any, taskIds?: Set<string>) {
  return (data.ghost_blocks || [])
    .filter((block: any) => !taskIds || taskIds.has(block.task_id))
    .map((block: any) => ({
      id: block.id,
      taskId: block.task_id,
      proposedStart: block.proposed_start,
      proposedEnd: block.proposed_end,
      cost: block.cost,
      confidence: block.confidence,
      reason: block.reason,
    }));
}

function normalizeCalendarEvents(events: any[] = []): CalendarEvent[] {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time,
      end: event.end || event.end_time,
      isImmovable: Boolean(event.is_immovable ?? true),
      isFlexHold: Boolean(event.is_flex_hold),
      stateProfile: event.state_profile || null,
      customAttributes: parseCustomAttributes(event.custom_attributes),
    }))
    .filter(isRenderableCalendarEvent);
}

function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return source !== 'canvas' && source !== 'book-tracker' && !attributes.canvas && !attributes.book_tracker;
}

function isAllDayCalendarEvent(event: { start?: string; end?: string }): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
